import type { Area } from "react-easy-crop";
import { createClientLogger } from "@/lib/logging/client";

const logger = createClientLogger("lib/image-utils");

export interface AspectRatioCheck {
  passes: boolean;
  dimensions: { width: number; height: number };
  actualAspect: number;
}

/**
 * Checks if an image's aspect ratio matches the target aspect ratio
 */
export const isAspectRatioValid = (
  imageWidth: number,
  imageHeight: number,
  targetAspect: number,
): boolean => {
  const imageAspect = imageWidth / imageHeight;
  // Use a small epsilon for floating-point comparison
  const epsilon = 0.01;
  return Math.abs(imageAspect - targetAspect) < epsilon;
};

/**
 * Loads an image file and returns the HTMLImageElement
 */
export const loadImage = (file: File): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to load image"));
    };
    img.src = url;
  });
};

/**
 * Loads an image from a URL and returns the HTMLImageElement
 */
export const loadImageFromUrl = (url: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    // `crossOrigin` puts the load on the CORS path, which a `blob:`/`data:` URL
    // has no business being on — these are locally created object URLs for a
    // file the user just picked, and the attribute only adds a way to fail.
    if (!/^(blob:|data:)/i.test(url)) {
      img.crossOrigin = "anonymous";
    }
    img.onload = () => resolve(img);
    img.onerror = () =>
      reject(
        new Error(
          "This image could not be opened. It may be in a format this device cannot read.",
        ),
      );
    img.src = url;
  });
};

/**
 * Checks if a file's aspect ratio matches the target
 */
export const checkAspectRatio = async (
  file: File,
  targetAspect?: number,
): Promise<AspectRatioCheck> => {
  const img = await loadImage(file);
  const actualAspect = img.width / img.height;

  return {
    passes: targetAspect
      ? isAspectRatioValid(img.width, img.height, targetAspect)
      : true,
    dimensions: { width: img.width, height: img.height },
    actualAspect,
  };
};

/**
 * Ceiling on the cropped canvas, in either dimension.
 *
 * A phone camera photo is routinely 4000px+ on the long edge, so a crop of it at
 * literal source resolution asks for a canvas of 9–80 megapixels. Chromium on
 * Android will not back a canvas past the device's max texture size, and when
 * that allocation fails there is no exception — `toBlob` just hands back `null`.
 * That was the whole "tap Done, brief Processing…, back to Done" failure: a
 * rejected promise the crop modal swallowed. iOS decodes the same photos into a
 * much smaller default capture size, which is why it only showed up on Android.
 *
 * 4096 is the smallest max texture size still in the field, and it is far above
 * anything the upload pipeline keeps: `optimizeImage` downsizes to 2048 next.
 */
const MAX_CROP_DIMENSION = 4096;

/**
 * Creates a cropped image file from an image URL and crop area (react-easy-crop
 * format). Keeps the source resolution unless it exceeds MAX_CROP_DIMENSION, in
 * which case it scales down to stay inside what the device can actually encode.
 */
export const getCroppedImgFromArea = async (
  imageSrc: string,
  pixelCrop: Area,
  fileName: string,
): Promise<File> => {
  const image = await loadImageFromUrl(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    throw new Error("This device could not prepare the image for cropping.");
  }

  // react-easy-crop derives the crop rect from the measured container, so a
  // frame where it has not been laid out yet yields NaN. Math.max(1, NaN) is
  // NaN, which becomes a 0x0 canvas whose toBlob returns null — reported to the
  // viewer as "this photo is too large", which it is not. Fall back to the
  // whole image and keep the rect inside it.
  const naturalWidth = image.naturalWidth || image.width;
  const naturalHeight = image.naturalHeight || image.height;
  const usableRect = (value: number, fallback: number) =>
    Number.isFinite(value) && value >= 1 ? Math.round(value) : fallback;
  const offset = (value: number) =>
    Number.isFinite(value) && value > 0 ? Math.round(value) : 0;

  const cropX = Math.min(offset(pixelCrop.x), Math.max(naturalWidth - 1, 0));
  const cropY = Math.min(offset(pixelCrop.y), Math.max(naturalHeight - 1, 0));
  const sourceWidth = Math.min(
    usableRect(pixelCrop.width, naturalWidth),
    Math.max(naturalWidth - cropX, 1),
  );
  const sourceHeight = Math.min(
    usableRect(pixelCrop.height, naturalHeight),
    Math.max(naturalHeight - cropY, 1),
  );
  const scale = Math.min(
    1,
    MAX_CROP_DIMENSION / Math.max(sourceWidth, sourceHeight),
  );
  const targetWidth = Math.max(1, Math.round(sourceWidth * scale));
  const targetHeight = Math.max(1, Math.round(sourceHeight * scale));

  canvas.width = targetWidth;
  canvas.height = targetHeight;

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";

  // Draw the cropped portion of the image
  ctx.drawImage(
    image,
    cropX,
    cropY,
    sourceWidth,
    sourceHeight,
    0,
    0,
    targetWidth,
    targetHeight,
  );

  return new Promise((resolve, reject) => {
    // 0.92 rather than 1.0: lossless JPEG output of a multi-megapixel canvas is
    // both the heaviest step in the encode and indistinguishable downstream,
    // since optimizeImage re-encodes at 0.92 anyway.
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(
            new Error(
              "This photo is too large for this device to crop. Try a smaller one.",
            ),
          );
          return;
        }
        const file = new File([blob], fileName, { type: "image/jpeg" });
        resolve(file);
      },
      "image/jpeg",
      0.92,
    );
  });
};

/**
 * Optimizes an image file by resizing it using the Native Canvas API.
 * Focuses on downsizing to save bandwidth before client-side AVIF/WebP encoding.
 */
export const optimizeImage = async (
  file: File,
  options: {
    maxWidth?: number;
    maxHeight?: number;
    quality?: number;
  } = {},
): Promise<File> => {
  const { maxWidth = 2048, maxHeight = 2048, quality = 0.92 } = options;

  try {
    const img = await loadImage(file);
    let { width, height } = img;

    // Calculate new dimensions while preserving aspect ratio
    if (width > maxWidth || height > maxHeight) {
      const ratio = Math.min(maxWidth / width, maxHeight / height);
      width = Math.round(width * ratio);
      height = Math.round(height * ratio);
    }

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Could not get canvas context");

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(img, 0, 0, width, height);

    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error("Canvas is empty"));
            return;
          }
          const baseName = file.name.replace(/\.[^/.]+$/, "");
          const optimizedFile = new File([blob], `${baseName}.jpg`, {
            type: "image/jpeg",
            lastModified: Date.now(),
          });
          resolve(optimizedFile);
        },
        "image/jpeg",
        quality,
      );
    });
  } catch (error) {
    logger.error("Optimization failed:", error);
    return file;
  }
};

/**
 * Formats aspect ratio for display (e.g., 1.777... => "16:9")
 */
export const formatAspectRatio = (aspect: number): string => {
  if (aspect === 1) return "1:1";
  if (Math.abs(aspect - 16 / 9) < 0.01) return "16:9";
  if (Math.abs(aspect - 9 / 16) < 0.01) return "9:16";
  if (Math.abs(aspect - 4 / 3) < 0.01) return "4:3";
  if (Math.abs(aspect - 3 / 2) < 0.01) return "3:2";
  return aspect.toFixed(2);
};

/**
 * Loads a video file and returns metadata (dimensions, duration) using Mediabunny
 */
export const loadVideo = async (
  file: File,
): Promise<{
  video?: HTMLVideoElement;
  width: number;
  height: number;
  duration: number;
}> => {
  const { Input, BlobSource, ALL_FORMATS } = await import("mediabunny");
  const input = new Input({
    source: new BlobSource(file),
    formats: ALL_FORMATS,
  });

  try {
    const videoTrack = await input.getPrimaryVideoTrack();
    if (!videoTrack) {
      throw new Error("No video track found");
    }
    const width = await videoTrack.getDisplayWidth();
    const height = await videoTrack.getDisplayHeight();
    const duration = await input.computeDuration();
    return {
      width,
      height,
      duration,
    };
  } finally {
    input.dispose();
  }
};

/**
 * Checks if a media file's (image or video) aspect ratio matches the target
 */
export const checkMediaAspectRatio = async (
  file: File,
  targetAspect: number,
): Promise<AspectRatioCheck> => {
  const isVideo = file.type.startsWith("video/");

  if (isVideo) {
    const { width, height } = await loadVideo(file);
    const actualAspect = width / height;

    return {
      passes: isAspectRatioValid(width, height, targetAspect),
      dimensions: { width, height },
      actualAspect,
    };
  }

  // For images, use the existing function
  return checkAspectRatio(file, targetAspect);
};

/**
 * Loads a video from URL and returns metadata/video handle using Mediabunny
 */
export const loadVideoFromUrl = async (
  url: string,
): Promise<{
  width: number;
  height: number;
  duration: number;
}> => {
  const { Input, UrlSource, ALL_FORMATS } = await import("mediabunny");
  const input = new Input({
    source: new UrlSource(url),
    formats: ALL_FORMATS,
  });

  try {
    const videoTrack = await input.getPrimaryVideoTrack();
    if (!videoTrack) {
      throw new Error("No video track found");
    }
    const width = await videoTrack.getDisplayWidth();
    const height = await videoTrack.getDisplayHeight();
    const duration = await input.computeDuration();
    return {
      width,
      height,
      duration,
    };
  } finally {
    input.dispose();
  }
};

/**
 * Creates a cropped image file from a video's first frame using Mediabunny CanvasSink.
 * Outputs at full resolution to preserve quality.
 */
export const getCroppedVideoFrame = async (
  videoSrc: string,
  pixelCrop: Area,
  fileName: string,
): Promise<File> => {
  const { Input, UrlSource, ALL_FORMATS, CanvasSink } = await import(
    "mediabunny"
  );
  const input = new Input({
    source: new UrlSource(videoSrc),
    formats: ALL_FORMATS,
  });

  try {
    const videoTrack = await input.getPrimaryVideoTrack();
    if (!videoTrack) {
      throw new Error("No video track found");
    }

    const sink = new CanvasSink(videoTrack, {
      crop: {
        left: pixelCrop.x,
        top: pixelCrop.y,
        width: pixelCrop.width,
        height: pixelCrop.height,
      },
    });

    const wrappedCanvas = await sink.getCanvas(0);
    if (!wrappedCanvas) {
      throw new Error("Failed to crop video frame");
    }

    const canvas = wrappedCanvas.canvas;
    const blob = await new Promise<Blob | null>((resolve) => {
      if (canvas instanceof HTMLCanvasElement) {
        canvas.toBlob((b) => resolve(b), "image/jpeg", 1.0);
      } else {
        canvas
          .convertToBlob({ type: "image/jpeg", quality: 1.0 })
          .then(resolve)
          .catch(() => resolve(null));
      }
    });

    if (!blob) {
      throw new Error("Failed to encode cropped video frame to blob");
    }

    return new File([blob], fileName, { type: "image/jpeg" });
  } finally {
    input.dispose();
  }
};
